import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, FileBadge, Plus, Sparkles, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
import type {
  AssetEntityType,
  AssetTemplate,
} from "@/components/TemplateSelector";

const ENTITY_TYPES: { value: AssetEntityType; label: string }[] = [
  { value: "vehicle", label: "Vehicle" },
  { value: "animal", label: "Animal" },
  { value: "pasture", label: "Pasture" },
  { value: "implement", label: "Implement" },
  { value: "tool", label: "Tool" },
  { value: "garden", label: "Garden / Structure" },
];

export function TemplatesPage() {
  const [templates, setTemplates] = useState<AssetTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "ranch" | "system">("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState<AssetEntityType>("vehicle");
  const [formDescription, setFormDescription] = useState("");

  const fetchTemplates = async () => {
    try {
      const data = await authFetch<{ items: AssetTemplate[] }>(
        `/api/templates?include_system=true`,
      );
      setTemplates(data.items ?? []);
      setError("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load templates";
      setError(msg);
      console.error("[Templates]", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      const matchSearch =
        !search ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        (t.description?.toLowerCase().includes(search.toLowerCase()) ?? false);
      const matchTab =
        tab === "all" ||
        (tab === "ranch" && t.is_system !== 1) ||
        (tab === "system" && t.is_system === 1);
      return matchSearch && matchTab;
    });
  }, [templates, search, tab]);

  const groupedByType = useMemo(() => {
    const groups: Record<string, AssetTemplate[]> = {};
    for (const t of filtered) {
      const key = t.asset_entity_type;
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    }
    return groups;
  }, [filtered]);

  const resetForm = () => {
    setFormName("");
    setFormType("vehicle");
    setFormDescription("");
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const created = await authFetch<AssetTemplate>("/api/templates", {
        method: "POST",
        body: JSON.stringify({
          name: formName.trim(),
          asset_entity_type: formType,
          description: formDescription.trim() || undefined,
        }),
      });
      toast.success(`Created "${created.name}"`);
      setDialogOpen(false);
      resetForm();
      await fetchTemplates();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Create failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClone = async (t: AssetTemplate) => {
    try {
      const cloned = await authFetch<AssetTemplate>(
        `/api/templates/${t.id}/clone`,
        { method: "POST", body: JSON.stringify({}) },
      );
      toast.success(`Cloned to "${cloned.name}"`);
      await fetchTemplates();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Clone failed";
      toast.error(msg);
    }
  };

  const handleDelete = async (t: AssetTemplate) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await authFetch(`/api/templates/${t.id}`, { method: "DELETE" });
      toast.success(`Deleted "${t.name}"`);
      await fetchTemplates();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Delete failed";
      toast.error(msg);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-6xl">
      <PageHeader
        title="Asset Templates"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Templates" }]}
      >
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              New template
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New asset template</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tpl-name">Name *</Label>
                <Input
                  id="tpl-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Hay Baler"
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-type">Asset type *</Label>
                <Select
                  value={formType}
                  onValueChange={(v) => setFormType(v as AssetEntityType)}
                >
                  <SelectTrigger id="tpl-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-desc">Description</Label>
                <Textarea
                  id="tpl-desc"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="What does this template represent?"
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Creating…" : "Create template"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Input
          placeholder="Search by name or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="ranch">My ranch</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <CardGridSkeleton count={6} />
      ) : error ? (
        <Card className="p-6 text-center">
          <p className="text-destructive">{error}</p>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileBadge}
          title="No templates yet"
          description={
            tab === "system"
              ? "No system templates match your search."
              : "Create your first template to standardize how new assets are set up."
          }
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedByType).map(([type, items]) => (
            <div key={type}>
              <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">
                {ENTITY_TYPES.find((e) => e.value === type)?.label ?? type} ({items.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((t) => (
                  <Card key={t.id} className="p-4 group flex flex-col">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <Link
                        to={`/templates/${t.id}`}
                        className="font-semibold text-sm hover:underline truncate flex-1 min-w-0"
                      >
                        {t.name}
                      </Link>
                      {t.is_system === 1 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-burnt-orange/10 text-burnt-orange dark:bg-burnt-orange-dark/15 dark:text-burnt-orange-dark shrink-0"
                          title="System template — read only. Clone to customize."
                        >
                          <Sparkles className="h-3 w-3" />
                          System
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
                        {t.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between gap-2 pt-3 mt-3 border-t opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link to={`/templates/${t.id}`}>
                        <Button size="sm" variant="ghost">
                          View
                        </Button>
                      </Link>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleClone(t)}
                          title="Clone"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        {t.is_system !== 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(t)}
                            title="Delete"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
